import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ShieldCheck, Loader2, Check, X, Building2, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

// Status -> badge styling (mirrors the dark theme accent tokens used elsewhere).
const STATUS_BADGE = {
  pending: 'bg-amber-500/15 text-amber-300',
  active: 'bg-green-500/15 text-green-300',
  rejected: 'bg-red-500/15 text-red-300',
  invited: 'bg-blue-500/15 text-blue-300',
};

const StatusBadge = ({ status }) => (
  <span
    className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
      STATUS_BADGE[status] || 'bg-white/10 text-slate-200'
    }`}
    data-testid={`user-status-badge`}
  >
    {status}
  </span>
);

const Admin = () => {
  const { token, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  // P6.7: delete-confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isSuperAdmin) fetchUsers();
    else setLoading(false);
  }, [isSuperAdmin, fetchUsers]);

  const updateStatus = async (user, status) => {
    if (actingId) return;
    setActingId(user.id);
    try {
      const response = await axios.put(
        `${API_URL}/admin/users/${user.id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update in place so the list doesn't jump around.
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...response.data } : u)));
      toast.success(status === 'active' ? `Approved ${user.email}` : `Rejected ${user.email}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update user';
      toast.error(message);
    } finally {
      setActingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/admin/users/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      toast.success(`Deleted ${deleteTarget.email}`);
      setDeleteTarget(null);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete user';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  // Which action buttons to show for a given user's current status.
  const renderActions = (user) => {
    if (user.is_super_admin) {
      return <span className="text-xs text-slate-400">Super-admin</span>;
    }

    const busy = actingId === user.id;
    const approveBtn = (
      <Button
        size="sm"
        onClick={() => updateStatus(user, 'active')}
        disabled={busy}
        className="bg-green-600 hover:bg-green-700 h-10 flex-1 sm:flex-none sm:h-9"
        data-testid={`approve-btn-${user.id}`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
        Approve
      </Button>
    );
    const rejectBtn = (
      <Button
        size="sm"
        variant="outline"
        onClick={() => updateStatus(user, 'rejected')}
        disabled={busy}
        className="border-red-500/40 text-red-300 hover:bg-red-500/15 h-10 flex-1 sm:flex-none sm:h-9"
        data-testid={`reject-btn-${user.id}`}
      >
        <X className="h-4 w-4 mr-1" />
        Reject
      </Button>
    );
    // P6.7: Delete is available for every non-super-admin user, alongside approve/reject.
    const deleteBtn = (
      <Button
        size="sm"
        variant="outline"
        onClick={() => setDeleteTarget(user)}
        disabled={busy || deleting}
        className="border-white/15 text-slate-300 hover:text-red-300 hover:bg-red-500/15 h-10 flex-1 sm:flex-none sm:h-9"
        data-testid={`delete-btn-${user.id}`}
      >
        <Trash2 className="h-4 w-4 mr-1" />
        Delete
      </Button>
    );

    let statusBtns = null;
    if (user.status === 'pending') statusBtns = <>{approveBtn}{rejectBtn}</>;
    else if (user.status === 'rejected') statusBtns = approveBtn;
    else if (user.status === 'active') statusBtns = rejectBtn;
    // invited (Team flow) — no approve/reject here, but delete is still allowed.

    return <>{statusBtns}{deleteBtn}</>;
  };

  const pendingCount = users.filter((u) => u.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="admin-page">
      <Card className="world-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box shrink-0">
              <ShieldCheck className="h-5 w-5 text-blue-400" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-xl text-white">User Management</CardTitle>
              <CardDescription className="text-slate-400">
                Approve or reject sign-ups across all companies
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!isSuperAdmin ? (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="not-super-admin">
              <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <ShieldCheck className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Restricted</h3>
              <p className="text-slate-400 max-w-md">This area is reserved for the platform administrator.</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <ShieldCheck className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No users yet</h3>
            </div>
          ) : (
            <>
              {pendingCount > 0 && (
                <div
                  className="mb-4 flex items-center gap-2 text-sm text-amber-300"
                  data-testid="pending-count"
                >
                  <Clock className="h-4 w-4" />
                  {pendingCount} awaiting approval
                </div>
              )}
              {/* Mobile-first: stacked cards, never a horizontally-scrolling table. */}
              <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="rounded-lg border border-white/10 bg-white/[0.03] p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                    data-testid={`admin-user-${user.id}`}
                  >
                    {/* Identity block */}
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-full bg-blue-500/15 flex items-center justify-center shrink-0">
                        <span className="text-sm font-medium text-blue-400">
                          {user.email[0].toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-white break-all">{user.email}</p>
                          <StatusBadge status={user.status} />
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                          <Building2 className="h-3 w-3 shrink-0" />
                          <span className="break-all">{user.company_name || '—'}</span>
                          <span className="text-slate-600">·</span>
                          <span className="whitespace-nowrap">
                            {new Date(user.created_at).toLocaleDateString()}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Actions — full-width touch targets on mobile, inline on desktop */}
                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                      {renderActions(user)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* P6.7: irreversible delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This will permanently delete <span className="font-medium text-slate-200">{deleteTarget?.email}</span>
                {deleteTarget?.company_name ? <> ({deleteTarget.company_name})</> : null} and their associated
                searches, leads, decisions and enrichments.
              </span>
              <span className="block text-red-300 font-medium">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="delete-confirm-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete user'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Admin;
